/**
 * PIP-343 — recognise Ticketmaster resale (TicketsNow, `tmr`) listings.
 *
 * TM's Discovery API tags resale anchors with `source: 'tmr'` (PIP-325
 * persists it as `Event.sourceProvenance`), and their event `url` is a
 * marketplace page: `https://www.ticketmaster.com/event/Z…`. That page exists
 * only while resale listings exist, so it 404s whenever there are none. TM
 * bot-blocks headless checks, so the PIP-163 link checker cannot see those
 * 404s. A marketplace URL therefore must never be a primary "Get tickets"
 * destination (operator decision, PIP-343 escalation 2026-10-02).
 *
 * The ingest lane (data-pipeline) and the CTA derivation (backend-api) both
 * need the same answer to "is this a resale listing?", so it lives here once.
 */

/** `Event.sourceProvenance` value for a Ticketmaster resale (TicketsNow) listing. */
export const TICKETMASTER_RESALE_PROVENANCE = 'tmr';

/**
 * Ticketmaster storefront hosts, checked as the apex or a subdomain so a
 * suffix look-alike (`notticketmaster.com`) never matches.
 */
const TICKETMASTER_MARKETPLACE_APEXES: readonly string[] = ['ticketmaster.com', 'ticketmaster.ca'];

/**
 * A marketplace page path: `/event/<id>` where the id is Z-prefixed (the
 * TicketsNow id shape; primary-partner ids are bare hex, which never contains
 * a `Z`). Optional trailing slash; any query string is ignored because
 * `URL#pathname` excludes it.
 *
 * PIP-381 — the id body is the URL-safe base64 alphabet, `-` and `_`
 * included (`Z7r9jZ1A7-yFt`, `Z7r9jZ1AAZok_`). The narrower `[0-9A-Za-z]`
 * class missed 12 of 66 upcoming prod rows, which then kept a `buy` CTA into
 * a page that 404s.
 */
const MARKETPLACE_EVENT_PATH = /\/event\/Z[0-9A-Za-z_-]+\/?$/;

function hostMatchesApex(host: string, apex: string): boolean {
  return host === apex || host.endsWith(`.${apex}`);
}

/**
 * Whether `url` is a Ticketmaster resale-marketplace event page
 * (`ticketmaster.com/event/Z…`).
 *
 * Detection is deliberately broad on scheme (`http:` or `https:`): a false
 * positive only demotes a link from the primary CTA, while a false negative
 * sends a user to a dead page.
 */
export function isTicketmasterResaleMarketplaceUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  const host = parsed.hostname.toLowerCase();
  if (!TICKETMASTER_MARKETPLACE_APEXES.some((apex) => hostMatchesApex(host, apex))) {
    return false;
  }
  return MARKETPLACE_EVENT_PATH.test(parsed.pathname);
}

/** The columns {@link isTicketmasterResaleEvent} reads. */
export interface TicketmasterResaleSignals {
  readonly sourceProvenance?: string | null;
  readonly ticketUrl?: string | null;
}

/**
 * Whether an event row is a Ticketmaster resale listing: TM said so
 * (`sourceProvenance = 'tmr'`), or its stored `ticketUrl` is a marketplace
 * page regardless of what provenance says. The second clause covers rows
 * whose provenance was never stamped.
 */
export function isTicketmasterResaleEvent(event: TicketmasterResaleSignals): boolean {
  return (
    event.sourceProvenance === TICKETMASTER_RESALE_PROVENANCE ||
    isTicketmasterResaleMarketplaceUrl(event.ticketUrl)
  );
}

/** The columns {@link isNeverResoldListing} reads. */
export interface NeverResoldSignals extends TicketmasterResaleSignals {
  /** The stored `Event.neverResold` column (Prisma default `true`). */
  readonly neverResold?: boolean | null;
}

/**
 * WEB-1104 — the public `editorial.neverResold` verdict: "LOCI guarantees the
 * listing is from the primary issuer".
 *
 * The stored column defaults to `true` and no ingest lane writes `false`, so on
 * a Ticketmaster resale row it is a false claim. The verdict is therefore
 * derived at response time: a row {@link isTicketmasterResaleEvent} flags is
 * never "never resold", whatever the column holds. Deriving it here, rather
 * than correcting rows at ingest, makes every existing row honest the moment
 * this ships, with no backfill, and keeps it the exact complement of the
 * `resaleListing` flag (WEB-1101): both read the same predicate.
 *
 * A missing column (`null` / `undefined`) never asserts the guarantee.
 */
export function isNeverResoldListing(event: NeverResoldSignals): boolean {
  return event.neverResold === true && !isTicketmasterResaleEvent(event);
}
